import { Directive, OnDestroy } from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { DateAdapter } from '@angular/material/core';
import { Subscription } from 'rxjs';

/**
 * Auto-applies to EVERY `<mat-datepicker>` in the app (registered once, globally,
 * via the shared `MaterialModule` array). When the calendar opens it injects a
 * themed "Today" quick-select button as a footer into the calendar panel.
 * Clicking it selects today's date (through the datepicker's own selection model,
 * so it propagates to the bound form control) and closes the picker.
 *
 * No per-component HTML change is required — the selector matches the
 * `<mat-datepicker>` element itself, so it attaches wherever a datepicker is used.
 */
@Directive({
  selector: 'mat-datepicker',
  standalone: true,
})
export class DatepickerTodayDirective implements OnDestroy {
  private readonly sub: Subscription;

  constructor(
    private readonly datepicker: MatDatepicker<any>,
    private readonly dateAdapter: DateAdapter<any>,
  ) {
    this.sub = this.datepicker.openedStream.subscribe(() => this.injectTodayButton());
  }

  /** Append the Today footer button to the open calendar panel (once). */
  private injectTodayButton(): void {
    // The calendar overlay renders asynchronously after `opened` fires.
    setTimeout(() => {
      const content = document.querySelector('.mat-datepicker-content');
      if (!content || content.querySelector('.dp-today-action')) {
        return;
      }
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'dp-today-action';
      btn.textContent = 'Today';
      btn.addEventListener('click', () => this.selectToday());
      content.appendChild(btn);
    });
  }

  private selectToday(): void {
    try {
      // `today()` returns the date in the adapter's own type (native Date here),
      // so this stays correct regardless of the configured DateAdapter.
      this.datepicker.select(this.dateAdapter.today());
    } catch {
      /* adapter/model not ready — ignore */
    }
    this.datepicker.close();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}
